import { createFileRoute } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Content } from '@/components/ui/content';
import { CopyText } from '@/components/ui/copy-text';
import {
  ActionsButton,
  DataTable,
  type InferFetcherParams,
  type InferRow,
  SortableHeader,
} from '@/components/ui/data-table';
import type { Filter } from '@/components/ui/search-toolbar';
import { SearchToolbar } from '@/components/ui/search-toolbar';
import { useIsMobile } from '@/hooks/use-mobile';
import { useRouter } from '@/hooks/use-router';
import { meta } from '@/lib/meta';
import { usePermissions } from '@/services/auth';
import { screenKeys, useDeleteScreen, useScreens } from '@/services/screens';

export const Route = createFileRoute('/_app/screens/')({
  head: () => meta({ label }),
  component: RouteComponent,
});

type Model = InferRow<typeof useScreens>;
const model = 'screens';
const label = 'Telas';

function RouteComponent() {
  const isMobile = useIsMobile();
  const { push } = useRouter();
  const { createPermissions } = usePermissions();
  const { mutateAsync: remove } = useDeleteScreen();

  const { canCreate, canRead, canUpdate, canDelete } = createPermissions(model);

  const [search, setSearch] = useState<InferFetcherParams<typeof useScreens>>({});

  const handleAdd = () => push(`/${model}/create`);
  const handleEdit = (id: string) => push(`/${model}/${id}`);
  const handleView = (id: string) => push(`/${model}/${id}/view`);
  const handleRemove = async (id: string) => await remove({ id });

  const filters: Filter<InferFetcherParams<typeof useScreens>>[] = [
    { type: 'text', label: 'Recurso', name: 'resource' },
    {
      type: 'select',
      label: 'Ativo?',
      name: 'isActive',
      options: [
        { label: 'Todos', value: 'all' },
        { label: 'Sim', value: 'true' },
        { label: 'Não', value: 'false' },
      ],
    },
  ];

  const columns: ColumnDef<Model>[] = [
    {
      accessorKey: 'resource',
      header: ({ column }) => <SortableHeader column={column} label="Recurso" />,
    },
    {
      accessorKey: 'label',
      header: ({ column }) => <SortableHeader column={column} label="Label" />,
    },
    {
      accessorKey: 'showInMenu',
      header: 'No menu?',
      cell: ({ row }) => (
        <Badge variant={row.getValue('showInMenu') ? 'success' : 'secondary'}>
          {row.getValue('showInMenu') ? 'Sim' : 'Não'}
        </Badge>
      ),
    },
    {
      accessorKey: 'isActive',
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant={row.getValue('isActive') ? 'success' : 'destructive'}>
          {row.getValue('isActive') ? 'Ativo' : 'Inativo'}
        </Badge>
      ),
    },
    {
      id: 'id',
      accessorKey: 'id',
      header: 'Cód.',
      cell: ({ row }) => {
        const id = row.getValue<string>('id');
        return <CopyText value={id} label={`${id.slice(0, 8)}...`} />;
      },
    },
  ];

  columns[isMobile ? 'unshift' : 'push']({
    id: 'actions',
    header: () => <div className="flex items-center justify-end">Ações</div>,
    enableHiding: false,
    cell: ({ row }) => {
      const data = row.original;
      return (
        <ActionsButton
          key={row.id}
          id={data.id}
          edit={canUpdate}
          view={canRead}
          remove={canDelete}
          onEdit={handleEdit}
          onView={handleView}
          onRemove={handleRemove}
          queryKey={screenKeys.list(search)}
        />
      );
    },
    meta: { sticky: isMobile ? 'left' : 'right' },
  });

  return (
    <Content
      breadcrumbs={{
        title: label,
        action: canCreate && (
          <Button variant="success" onClick={handleAdd} icon="plus">
            Adicionar
          </Button>
        ),
        items: [{ label: 'Listagem', active: true }],
      }}
    >
      <SearchToolbar filters={filters} onSearch={setSearch} />
      <DataTable columns={columns} useFetcher={useScreens} search={search} />
    </Content>
  );
}
